import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { useLang } from '../i18n/LangContext.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

// Allow admins to size and highlight text via inline <span style="…"> / <small>.
// Everything else stays at rehype-sanitize defaults, so scripts, iframes and
// event handlers are stripped even though raw HTML is now parsed.
const SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: [...defaultSchema.tagNames, 'span', 'small'],
  attributes: {
    ...defaultSchema.attributes,
    span:  [...(defaultSchema.attributes.span || []), 'style', 'className'],
    small: ['style', 'className'],
  },
};

function resolveMediaUrl(src) {
  if (!src) return '';
  if (src.startsWith('/uploads/')) return API_BASE + src;
  return src;
}

const MD_COMPONENTS = {
  p: ({ children }) => (
    <p style={{ fontFamily: 'var(--body)', fontSize: 19, lineHeight: 1.7, color: 'var(--slate)', margin: '0 0 16px', textAlign: 'justify' }}>{children}</p>
  ),
  strong: ({ children }) => (
    <strong style={{ color: 'var(--slate)', fontWeight: 600 }}>{children}</strong>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontFamily: 'var(--display)', fontWeight: 500, fontSize: 'clamp(22px, 2.2vw, 30px)', lineHeight: 1.3, margin: '44px 0 16px', letterSpacing: '-0.008em', color: 'var(--slate)' }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontFamily: 'var(--display)', fontWeight: 500, fontSize: 'clamp(21px, 1.6vw, 24px)', lineHeight: 1.3, margin: '36px 0 12px', letterSpacing: '-0.005em', color: 'var(--slate)' }}>{children}</h3>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: 24, margin: '16px 0', fontFamily: 'var(--body)', fontSize: 19, lineHeight: 1.7, color: 'var(--slate)', textAlign: 'justify' }}>{children}</ol>
  ),
  ul: ({ children }) => (
    <ul style={{ paddingLeft: 24, margin: '16px 0', fontFamily: 'var(--body)', fontSize: 19, lineHeight: 1.7, color: 'var(--slate)', textAlign: 'justify' }}>{children}</ul>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: 6 }}>{children}</li>
  ),
  hr: () => (
    <hr style={{ border: 0, height: 1, background: 'var(--bronze-30)', margin: '40px 0' }} />
  ),
  img: ({ src, alt }) => (
    <img src={resolveMediaUrl(src)} alt={alt || ''} loading="lazy"
      style={{ display: 'block', maxWidth: '100%', height: 'auto', margin: '32px auto', border: '1px solid var(--bronze-30)' }} />
  ),
  a: ({ href, children }) => (
    <a href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      style={{ color: 'var(--bronze)', textDecoration: 'none', borderBottom: '1px solid var(--bronze-30)' }}>{children}</a>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{ margin: '28px 0', padding: '8px 0 8px 24px', borderLeft: '2px solid var(--bronze)', color: 'var(--slate-70)', fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: 20, lineHeight: 1.6 }}>{children}</blockquote>
  ),
  code: ({ inline, children }) => inline ? (
    <code style={{ fontFamily: 'var(--label)', fontSize: '0.9em', background: 'var(--bone)', padding: '2px 6px', borderRadius: 2, color: 'var(--slate)' }}>{children}</code>
  ) : (
    <code style={{ fontFamily: 'var(--label)', fontSize: 14, lineHeight: 1.6 }}>{children}</code>
  ),
  pre: ({ children }) => (
    <pre style={{ background: 'var(--bone)', border: '1px solid var(--bronze-30)', padding: '16px 20px', margin: '24px 0', overflowX: 'auto', fontFamily: 'var(--label)', fontSize: 14, lineHeight: 1.6, color: 'var(--slate)' }}>{children}</pre>
  ),
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '28px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--body)', fontSize: 16, color: 'var(--slate)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead style={{ borderBottom: '2px solid var(--bronze)', background: 'var(--bone)' }}>{children}</thead>
  ),
  th: ({ children }) => (
    <th style={{ textAlign: 'left', padding: '12px 16px', fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 500, color: 'var(--slate)' }}>{children}</th>
  ),
  td: ({ children }) => (
    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--bronze-30)', lineHeight: 1.6, verticalAlign: 'top' }}>{children}</td>
  ),
};

// Shared single-language article renderer (pagehead + Markdown body).
// `item` is a single-language record: { lang, cat, date, title, body_md, updated_at }.
// Because content is single-language, switching the site language on a detail
// page bounces back to the list in the newly selected language.
export default function ArticleView({ item, backTo = '/insights' }) {
  const { lang } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    if (item && item.lang && lang !== item.lang) {
      navigate(backTo);
    }
  }, [lang, item, backTo, navigate]);

  if (!item) return null;

  return (
    <article>
      <section className="pagehead" style={{ paddingBottom: 'clamp(40px, 5vw, 64px)' }}>
        <div className="wrap" style={{ maxWidth: 760 }}>
          <div className="pagehead__kicker" style={{ marginBottom: 'clamp(20px, 2.5vw, 28px)' }}>
            <span className="label" style={{ color: 'var(--slate-70)' }}>{item.date}</span>
          </div>
          <h1 style={{
            fontFamily: 'var(--display)', fontWeight: 400,
            fontSize: 'clamp(32px, 4vw, 52px)', lineHeight: 1.15,
            letterSpacing: '-0.012em', margin: 0, textWrap: 'balance',
          }}>{item.title}</h1>
          {/* series/category (item.cat) temporarily hidden on the frontend */}
        </div>
      </section>

      <section className="section" style={{ background: 'var(--white)', paddingTop: 0 }}>
        <div className="article-body" style={{ maxWidth: 680, margin: '0 auto', paddingTop: 'clamp(40px, 5vw, 64px)' }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA]]}
            components={MD_COMPONENTS}>
            {item.body_md || ''}
          </ReactMarkdown>
        </div>
      </section>
    </article>
  );
}
